import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/profile-sharing'

export const profileSharingTutorial: Tutorial = {
  slug: 'profile-sharing',
  title: 'Profile Sharing',
  subtitle: 'Invite someone in without handing over the whole profile',
  date: '2026-07-09',
  category: 'Sharing & Support',
  platform: 'mobile',
  tags: ['Profile Sharing', 'collaborators', 'permissions', 'privacy'],
  description:
    'Invite family, a coach, or a healthcare provider to see exactly what you choose — per category, time-boxed, and revocable at any time. Available on every plan.',
  highlights: [
    'Three-step wizard: who, what, then a plain-language review before anything is sent.',
    'Each category toggles independently — Goals, Records, Coach, Basic Profile.',
    'Access expiration defaults to time-boxed; nothing is visible until the recipient accepts.',
  ],
  coverImage: `${IMG}/01_sharing_empty_state.png`,
  sections: [
    {
      heading: 'Where it starts',
      paragraphs: [
        'From a profile, **Sharing** shows three tabs: what you\'ve shared, what\'s been shared with you, and an audit log of who accessed what. A fresh profile starts empty with a clear call to action.',
      ],
      image: {
        src: `${IMG}/01_sharing_empty_state.png`,
        alt: 'Profile sharing empty state with Share your profile button',
        caption: 'Sharing empty state',
      },
    },
    {
      heading: 'Step 1: who',
      paragraphs: [
        'Tapping **Share your profile** opens a short three-step wizard. First, their email and a relationship label — Family, Friend, Coach, Healthcare provider, or Other — plus an optional personal note.',
      ],
      image: {
        src: `${IMG}/02_step_who.png`,
        alt: 'Profile sharing step 1: recipient email and relationship',
        caption: 'Step 1: who you\'re sharing with',
      },
    },
    {
      heading: 'Step 2: what',
      paragraphs: [
        'Sharing isn\'t all-or-nothing — each category (Basic Profile, Wellness Goals, Health Records, Wellness Coach) toggles independently, and documents are never included in sharing at all.',
        'An access-expiration control (7/30/90 days, 1 year, or no expiration) sits right below, so a share doesn\'t have to be permanent by default.',
      ],
      image: {
        src: `${IMG}/03_step_what_permissions.png`,
        alt: 'Profile sharing step 2: per-category permission toggles',
        caption: 'Step 2: choosing what\'s visible',
      },
    },
    {
      heading: 'Step 3: review',
      paragraphs: [
        'Before anything is sent, a summary lays out exactly what was chosen — recipient, relationship, expiration, and each shared category with its access level.',
        'Nothing is visible to the recipient until they accept the email invite — and access can be revoked at any time from the Sharing screen.',
      ],
      image: {
        src: `${IMG}/04_step_review.png`,
        alt: 'Profile sharing step 3: review summary before sending',
        caption: 'Step 3: review before sending',
      },
    },
  ],
  whyMatters:
    'Most "family sharing" features in health apps are binary — you\'re either fully in or fully out. Livewello\'s per-category, time-boxed permissions mean you can give your doctor read access to lab results without also exposing your Wellness Coach conversations, or give a workout partner visibility into goals without health records.',
  relatedSlugs: ['accountability-partners', 'multi-profile'],
  meta: {
    description:
      'Profile Sharing in Livewello: per-category permissions, time-boxed access, and a three-step flow that makes granular sharing legible.',
    ogImage: `${IMG}/01_sharing_empty_state.png`,
  },
}
